import { TimelineLite, TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import { DestroyOptions } from "pixi.js";
import { Application } from "../Application";
import * as Constants from "../Data/Constants";
import { SPRITESHEET_GAME } from "../Data/Constants";
import { Culture } from "../Data/Culture";
import * as Topics from "../Data/Topics";
import AudioPreloadManager, { PRIORITY } from "../Utils/AudioPreloadManager";
import { CharNoteParticleSystem } from "./CharNoteParticleSystem";
import { CultureSwitchParticleBurst } from "./CultureSwitchParticleBurst";
import { IOnboardingTarget } from "./IOnboardingTarget";
import { ProgressBar } from "./ProgressBar";
import { SoloDebugInfo } from "./SoloDebugInfo";
import SpotlightFactory from "./SpotlightFactory";

export enum SpineTrack {
    Body,
    Eyes,
    Mouth,
    NumElements,
}

export enum CharacterState {
    Idle,
    Prepping,
    Solo,
    Supporting,
    Running,
    Swapping,
    NumElements,
}

export enum CharacterName {
    Carmen,
    Leo,
    Andy,
    // TODO Relish => Shift below to the end when the Andy file is fixed.
    NumElements,
}

const SPOTLIGHT_ALPHA_ON = 0.35;
const SPOTLIGHT_ALPHA_OFF = 0;
const SPOTLIGHT_FADE_IN_DURATION = 0.1;
const SPOTLIGHT_FADE_OUT_DURATION = 0.25;

export class Character extends HLF.Button implements HLF.Input.IFocusable, IOnboardingTarget {
    public get name() {
        return CharacterName[this._characterName];
    }
    /** Use `interactive` as the getter */
    public set inputEnabled(pEnabled: boolean) {
        this._inputEnabled = pEnabled;
        this.interactive = this._inputEnabled && !this._busy;
        if (!this.interactive && !!pEnabled) {
            console.warn(`failed to enable character ${this._characterName}`);
        }
        PubSub.publishSync(Topics.KEYBOARD_FOCUSABLE_INTERACTIVE_CHANGED, this);
    }

    /** Setter for `_busy`, with side effects */
    private set busy(pBusy: boolean) {
        this._busy = pBusy;
        this.interactive = this._inputEnabled && !this._busy;
        PubSub.publishSync(Topics.KEYBOARD_FOCUSABLE_INTERACTIVE_CHANGED, this);
    }

    public set endPosition(pPos: PIXI.Point) {
        this._endPosition.copy(pPos);
    }

    public get trueWidth(): number {
        return this._spineContainer.width;
    }

    public get trueHeight(): number {
        return this._spineContainer.height;
    }

    public get isPreppingOrSoloing(): boolean {
        return this._state === CharacterState.Prepping || this._state === CharacterState.Solo;
    }
    public get alternate() { return this._alternate; }

    public get isSoloLoaded(): boolean {
        return AudioPreloadManager.isFileLoaded(this._loadedSongName);
    }

    private static readonly ANIM_IDLE: string = "idle";
    private static readonly ANIM_SUPPORT: string = "idle";
    private static readonly ANIM_SOLO: string = "play";

    /** Are we in the middle of an animation, and as such not clickable */
    private _busy: boolean = false;

    /** Should we be clickable, if in the right animation */
    private _inputEnabled: boolean = true;

    private _state: CharacterState = CharacterState.Idle; // NEEDED?
    private readonly _characterName: CharacterName;
    private readonly _characterString: string;
    private readonly _spine: PIXI.spine.Spine;
    private readonly _spineContainer: PIXI.Container;
    private readonly _cultureChangeTimer: TimelineLite;
    private readonly _messageTokens: any[];
    private readonly _endPosition: PIXI.Point = new PIXI.Point();
    private readonly _noteParticleSystem: CharNoteParticleSystem;
    private readonly _swapBurstParticleSystem: CultureSwitchParticleBurst;
    private _currentCulture: Culture;
    private _loadedSongName: string = "";
    private _particleSpawnBone: PIXI.spine.core.Bone;
    private _queuedCulture: Culture = Culture.NumElements;
    private _cancelSolo: boolean = false;
    private readonly _hoverVO: string | undefined;
    private readonly _clickedSFX: string | undefined;
    private readonly _progressBar: ProgressBar;
    private readonly _debugParticlePosition: PIXI.Sprite;
    private readonly _spotlight: PIXI.Sprite;
    private readonly _spotlightGround: PIXI.Sprite;
    private _alternate: boolean = false;

    constructor(
        pCharacter: CharacterName,
        pParticleOffset: PIXI.Point,
        pInputOffset: PIXI.Point = new PIXI.Point(0, 0),
        pCulture: Culture,
        pHoverVO?: string,
        pClickedSFX?: string,
    ) {
        super(() => this.onInputDown());
        this._currentCulture = pCulture;
        this._hoverVO = pHoverVO;
        this._clickedSFX = pClickedSFX;
        this._cultureChangeTimer = new TimelineLite();

        this._spotlight = SpotlightFactory.MakeSpotlight();
        this._spotlight.scale = HLF.PointUtils.multiply(this._spotlight.scale, 0.9);
        this._spotlight.position.y += 25;
        this._spotlightGround = HLF.PixiUtils.makeSprite("spotlight-soloist-ground", SPRITESHEET_GAME);
        this._spotlightGround.anchor.set(0.5, 1);
        this._spotlightGround.position.y = this._spotlight.position.y + this._spotlight.height;
        this._spotlightGround.width = this._spotlight.width;
        this._spotlightGround.scale.y = 2;
        this._spotlightGround.alpha = 0;
        this.addChild(this._spotlightGround);
        this.addChild(this._spotlight);

        this._spineContainer = new PIXI.Container();
        this._spineContainer.scale.set(0.2);

        this._characterName = pCharacter;

        this._characterString = Character.convertNameToString(this._characterName);
        this._spine = HLF.PixiUtils.makeSpine(this._characterString);
        this._spine.autoUpdate = false;
        this._spine.position = pInputOffset.clone();
        this._spineContainer.addChild(this._spine);
        this.addChild(this._spineContainer);

        this.hitArea = new PIXI.Rectangle(
            -this._spineContainer.width * 0.5,
            -this._spineContainer.height * 0.5,
            this._spineContainer.width,
            this._spineContainer.height);

        this._spine.state.addListener({
            complete: this.onAnimComplete.bind(this),
        });

        this.playAnim(this.getAnimName(Character.ANIM_IDLE), SpineTrack.Body, true);
        // this.queueBlink();

        this._messageTokens = [
            PubSub.subscribe(Topics.CARMEN, this.onMessagePublished.bind(this)),
            PubSub.subscribe(Topics.GAME_PAUSED, this.onPause.bind(this)),
            PubSub.subscribe(Topics.GAME_UNPAUSED, this.onResume.bind(this)),
        ];

        this._noteParticleSystem = new CharNoteParticleSystem(pCulture);
        this._noteParticleSystem.position.copy(pParticleOffset);
        this.addChild(this._noteParticleSystem);

        if (this._characterName === CharacterName.Leo) {
            this._noteParticleSystem.updateAcceleration(85, -50);
        }

        this._swapBurstParticleSystem = new CultureSwitchParticleBurst(pCulture);
        this._swapBurstParticleSystem.position.copy(pParticleOffset);
        this.addChild(this._swapBurstParticleSystem);

        this._debugParticlePosition = HLF.PixiUtils.makeSprite("button-paint-fill", Constants.SPRITESHEET_UI);
        this._debugParticlePosition.scale.set(0.25, 0.25);
        this._debugParticlePosition.visible = Application.instance.debugMode;

        this._particleSpawnBone = this._spine.skeleton.findBone("particles_" + pCulture);
        this.addChild(this._debugParticlePosition);

        this._progressBar = new ProgressBar();
        this._progressBar.position.y = -this._spineContainer.height * 0.75;
        this.addChild(this._progressBar);

        this.onDebugStateChanged = this.onDebugStateChanged.bind(this);
        Application.instance.registerOnDebugModeChangedCallback(this.onDebugStateChanged);

        for (let i = 0; i < Culture.NumElements; i++) {
            if (i === this._currentCulture) {
                this.preloadSolo(i, this._alternate, PRIORITY.NEXT);
                this.preloadSolo(i, !this._alternate, PRIORITY.LATER);
            }
            else {
                this.preloadSolo(i, false, PRIORITY.EVENTUALLY);
                this.preloadSolo(i, true, PRIORITY.EVENTUALLY);
            }
        }

    }

    public static convertNameToString(pCharacterName: CharacterName): string {
        switch (pCharacterName) {
            case CharacterName.Andy:
                return "andy";
            case CharacterName.Leo:
                return "leo";
            case CharacterName.Carmen:
            default:
                return "carmen";
        }
    }
    public getOnboardingPosition(): PIXI.Point {
        return this._spine.getGlobalPosition();
    }
    public getOnboardingRotation(): number {
        return PIXI.PI_2 / 2;
    }

    public SpotlightOn() {
        TweenLite.killTweensOf(this._spotlight, false, {alpha: true});
        TweenLite.killTweensOf(this._spotlightGround, false, {alpha: true});
        TweenLite.to(this._spotlight, SPOTLIGHT_FADE_IN_DURATION, {alpha: SPOTLIGHT_ALPHA_ON});
        TweenLite.to(this._spotlightGround, SPOTLIGHT_FADE_IN_DURATION, {alpha: 1});
    }

    public SpotlightOff() {
        TweenLite.killTweensOf(this._spotlight, false, {alpha: true});
        TweenLite.killTweensOf(this._spotlightGround, false, {alpha: true});
        TweenLite.to(this._spotlight, SPOTLIGHT_FADE_OUT_DURATION, {alpha: SPOTLIGHT_ALPHA_OFF});
        TweenLite.to(this._spotlightGround, SPOTLIGHT_FADE_OUT_DURATION, {alpha: 0});
    }

    public onFocusBegin(): void {
        PubSub.publishSync(Topics.KEYBOARD_FOCUS_BEGIN, this);
        this.playHoverVO();
        this.SpotlightOn();
    }

    public onFocusActivated(): void {
        if (this.interactive) {
            this.onInputDown();
            this.playClickedSFX();
        }
    }

    public onFocusEnd(): void {
        PubSub.publishSync(Topics.KEYBOARD_FOCUS_END, this);
        if (!this.isPreppingOrSoloing) {
            this.SpotlightOff();
        }
    }

    public getFocusPosition(): PIXI.Point {
        return this.getGlobalPosition();
    }

    public getFocusSize(): PIXI.Point {
        return new PIXI.Point(this._spineContainer.width, this._spineContainer.height);
    }
    public update(pDeltaTime: number): void {
        if (this._spine !== undefined) {
            this._spine.update(pDeltaTime);
        }

        this._noteParticleSystem.updateFromGame(pDeltaTime);
        this._noteParticleSystem.updateSpawnPosition(new PIXI.Point(
            (this._spine.position.x + this._particleSpawnBone.worldX) * 0.2,
            (this._spine.position.y + this._particleSpawnBone.worldY) * 0.2,
        ));
        this._debugParticlePosition.position = new PIXI.Point(
            (this._spine.position.x + this._particleSpawnBone.worldX) * 0.2,
            (this._spine.position.y + this._particleSpawnBone.worldY) * 0.2,
        );
        this._swapBurstParticleSystem.updateFromGame(pDeltaTime);

        if (this._state === CharacterState.Prepping) {
            this._progressBar.progressValue += pDeltaTime;
        }
    }

    public startIntro(): void {
        let lastUpdate = new Date().getTime();

        this.position.copy(this._endPosition);
        this.visible = true;

        const introTimeline: TimelineLite = new TimelineLite();

        introTimeline.fromTo(this._spine, 0.2, { pixi: {
            scaleX: 0,
            scaleY: 0,
        }},
        {pixi: {
            scaleX: 1,
            scaleY: 1,
        }}, 0);

        introTimeline.delay(4);

        introTimeline.call(() => {
            if (!this.isPreppingOrSoloing) {
                this.switchState(CharacterState.Idle);
            }

            this.registerAsFocusable();
        });

        introTimeline.eventCallback(Constants.TWEEN_ONUPDATE, () => {
            const now = new Date().getTime();
            const deltaTime = now - lastUpdate;

            lastUpdate = now;
        });
    }

    public startSolo(pStartTime: number): void {
        // console.log(this._characterString + " starting solo.");
        this._progressBar.hide(true, () => this._progressBar.progressPerc = 0);
        this._noteParticleSystem.startEmitting();
        console.log("playing %s", this._loadedSongName);
        const track = Application.instance.audio.getAudioTrack(this._loadedSongName, Constants.AUDIO_CATEGORY_SOLO);
        track!.once(HLF.HowlerUtils.Events.END, () => PubSub.publishSync(Topics.SOLO_ENDED, this._characterName));
        track!.play();
        this.switchState(CharacterState.Solo);
        PubSub.publishSync(Topics.SOLO_STARTED, this._characterName);
        this.SpotlightOn();
    }

    public onResize(pSize: PIXI.Point): void {
        this.position.copy(this._endPosition);
    }

    public getTrackTimePos(): number {
        const track = Application.instance.audio.getAudioTrack(this._loadedSongName, Constants.AUDIO_CATEGORY_SOLO);

        if (track !== undefined) {
            return track.getTimePos();
        }
        else {
            return -1;
        }
    }

    public setTrackTimePos(pPos: number): void {
        const track = Application.instance.audio.getAudioTrack(this._loadedSongName, Constants.AUDIO_CATEGORY_SOLO);

        if (track !== undefined) {
            return track.setTimePos(pPos);
        }
    }

    public getAnimName(pAnim: string, pCulture?: number): string {
        return pAnim + "_" + (pCulture !== undefined ? pCulture : this._currentCulture);
    }
    /**
     * Called the moment a culture change is triggered
     * @param pNewCulture The culture to change to.
     * @param pFinishedCallback The callback to invoke when the change is complete.
     */
    public changeCulture(pNewCulture: Culture) {
        // Cache the culture to switch to when the put away animation completes
        this._queuedCulture = pNewCulture;

        if (this._state === CharacterState.Prepping) {
            // Because solos are loaded first and then played later, we need to flag that the upcoming solo should
            // be cancelled
            this._cancelSolo = true;
            this.SpotlightOff();
            // We also need to tell the game that a solo has been cancelled because the game determines when a solo
            // starts
            PubSub.publishSync(Topics.CANCEL_SOLO, undefined);
        }
        else if (this._state === CharacterState.Solo) {
            this.SpotlightOff();
            // stop particles, stop solo audio
            this._noteParticleSystem.stopEmitting();
            Application.instance.audio.stop(this._loadedSongName, Constants.AUDIO_CATEGORY_SOLO);
        }
        this._swapBurstParticleSystem.startEmitting(pNewCulture);
        // Start swap sequence
        this.switchState(CharacterState.Swapping);
    }

    public destroy(pOptions?: DestroyOptions | boolean): void {
        this._spine.destroy(pOptions);
        this._messageTokens.forEach(PubSub.unsubscribe);
        Application.instance.audio.stop(this._loadedSongName, Constants.AUDIO_CATEGORY_SOLO);
        Application.instance.unregisterOnDebugChangedCallback(this.onDebugStateChanged);
        this._cultureChangeTimer?.kill();
        TweenLite.killTweensOf(this._spotlight);
        super.destroy(pOptions);
    }

    public simulateClick(pForce: boolean = false): void {
        if (this.interactive || pForce) {
            this.onInputDown();
        }
    }

    public setProgressBarValues(pMin: number, pMax: number) {
        this._progressBar.setMinMaxValues(pMin, pMax);
        this._progressBar.progressPerc = 0;
    }
    protected onPointerOver(pEvent: PIXI.interaction.InteractionEvent): void {
        super.onPointerOver(pEvent);
        this.playHoverVO();
        this.SpotlightOn();
    }

    protected onPointerDown(pEvent: PIXI.interaction.InteractionEvent): void {
        super.onPointerDown(pEvent);
    }

    protected onPointerUp(pEvent: PIXI.interaction.InteractionEvent): void {
        if (this._eventData !== undefined) {
            // console.log(pEvent);
        }
        super.onPointerUp(pEvent);
    }

    protected onPointerOut(pEvent: PIXI.interaction.InteractionEvent): void {
        super.onPointerOut(pEvent);
        if (!this.isPreppingOrSoloing) {
            this.SpotlightOff();
        }
    }

    protected onPause(pTopic: string): void {
        Application.instance.audio.pause(this._loadedSongName, Constants.AUDIO_CATEGORY_SOLO);
    }

     protected onResume(pTopic: string): void {
        const track = Application.instance.audio.getAudioTrack(this._loadedSongName, Constants.AUDIO_CATEGORY_SOLO);

        if (track !== undefined) {
            if (track.getTimePos() > 0) {
                track.play();
            }
        }
     }

    private registerAsFocusable(): void {
        PubSub.publishSync(HLF.REGISTER_FOCUSABLE, this);
    }

    /**
     * Called when the take out instrument animation is finished.
     */
    private finishCultureChange(): void {
        this.switchState(CharacterState.Idle);
        PubSub.publishSync(Topics.INSTRUMENT_PULL_OUT_FINISHED, this._characterName);
    }

    private switchState(pNewState: CharacterState): void {
        // console.log(this._characterString + " SWITCHING FROM " + this.convertStateToString(this._state) +
        //     " TO " + this.convertStateToString(pNewState));
        const prevState = this._state;
        const dur: number = this.getTransitionDuration(this._characterName, this._currentCulture);
        this._state = pNewState;
        switch (this._state) {
            case CharacterState.Idle:
                this.busy = false;
                if (prevState !== CharacterState.Swapping) {
                    if (prevState === CharacterState.Solo) {
                        this.playAnim(this.getAnimName(Character.ANIM_IDLE), SpineTrack.Body, true, dur);
                    }
                    else {
                        this.playAnim(this.getAnimName(Character.ANIM_IDLE), SpineTrack.Body, true);
                    }
                }
                // this.queueBlink();
                break;
            case CharacterState.Prepping:
                this.busy = true;
                break;
            case CharacterState.Solo:
                this.busy = true;
                this.playAnim(this.getAnimName(Character.ANIM_SOLO), SpineTrack.Body, true, dur);
                break;
            case CharacterState.Supporting:
                this.busy = false; // i.e. allow multiple concurrent solos
                this.playAnim(this.getAnimName(Character.ANIM_SUPPORT), SpineTrack.Body, true, 0.15);
                break;
            case CharacterState.Running:
                this.busy = true;
                this.playAnim(this.getAnimName(Character.ANIM_IDLE), SpineTrack.Body, true);
                break;
            case CharacterState.Swapping:
                this.busy = true;
                this.changeInstruments();
                break;
            default:
                break;
        }
    }

    private getTransitionDuration(pCharacterName: CharacterName, pCulture: Culture): number {
        let dur: number = 0.15;
        switch (pCharacterName) {
            case CharacterName.Andy:
                dur = 0;
                break;
            case CharacterName.Carmen:
                if (pCulture === Culture.India) {
                    dur = 0;
                }
                break;
            default:
                break;
        }

        return dur;
    }

    private convertStateToString(pState: CharacterState): string {
        switch (pState) {
            case CharacterState.Prepping:
                return "Prepping";
            case CharacterState.Running:
                return "Running";
            case CharacterState.Solo:
                return "Solo";
            case CharacterState.Supporting:
                return "Supporting";
            case CharacterState.Swapping:
                return "Swapping";
            case CharacterState.Idle:
            default:
                return "Idle";
        }
    }

    private playAnim(pName: string, pTrack: number = SpineTrack.Body, pLoop: boolean = false,
                     pTransitionDuration: number = 0.15, pResetSkeleton: boolean = false): number {
        // if (this._blinkTimer !== undefined) {
        //     this._blinkTimer.kill();
        // }
        if (pResetSkeleton) {
            this._spine.skeleton.setToSetupPose();
            this._spine.state.tracks = [];
        }
        const entry = this._spine.state.setAnimation(pTrack, pName, pLoop);
        entry.mixDuration = pTransitionDuration;
        this._spine.update(0);
        return this._spine.state.getCurrent(pTrack).animationEnd;
    }

    private playHoverVO(): void {
        if (this._hoverVO !== undefined) {
            PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(this._hoverVO, 1, false, HLF.AudioCategory.VO));
        }
    }

    private playClickedSFX(): void {
        if (this._clickedSFX !== undefined) {
            PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(this._clickedSFX, 1, false, HLF.AudioCategory.SFX));
        }
    }

    private onAnimComplete(pTrack: PIXI.spine.core.TrackEntry): void {
        switch (pTrack.animation.name) {
            default:
                break;
        }
    }

    private changeInstruments(): void {
        this.playAnim(this.getAnimName(Character.ANIM_IDLE, this._queuedCulture), SpineTrack.Body, true);

        this._particleSpawnBone = this._spine.skeleton.findBone("particles_" + this._queuedCulture);

        PubSub.publishSync(Topics.INSTRUMENT_PULL_OUT_STARTED, this._characterName);

        this.finishCultureChange();

        // Clear the queuedCulture
        this._queuedCulture = Culture.NumElements;
    }

    private onMessagePublished(pTopic: string, pData?: any): void {
        switch (pTopic) {
            case Topics.INTRO_COMPLETED:
                this.onIntroCompleted();
                break;
            case Topics.CULTURE_CHANGE_COMPLETE:
                this.onCultureChangeComplete(pData);
                break;
            case Topics.CULTURE_CHANGE_BEGIN:
                this._progressBar.hide(true);
                break;
            case Topics.SOLO_STARTED:
                this.onSoloStart(pData);
                break;
            case Topics.SOLO_ENDED:
                this.onSoloEnd(pData);
                break;
            case Topics.INSTRUMENT_PULL_OUT_FINISHED:
                if (this.isPreppingOrSoloing === false) {
                    this.busy = false; // i.e. allow multiple soloists
                }
                break;
            default:
                break;
        }
    }

    private onInputDown(): void {
        this._cancelSolo = false;
        this.switchState(CharacterState.Prepping);

        AudioPreloadManager.UpdatePriority({
            character: this._characterName,
            culture: this._currentCulture,
            alternate: this._alternate,
            priority: PRIORITY.IMMEDIATELY,
        });

        this._loadedSongName = AudioPreloadManager.getFilename(
            this._currentCulture,
            this._characterName,
            this._alternate,
        );
        PubSub.publishSync(Topics.SOLO_REQUESTED, this._characterName);
        this._progressBar.show(true);

        AudioPreloadManager.UpdatePriority({
            culture: this._currentCulture,
            alternate: !this._alternate,
            priority: PRIORITY.SOON,
            character: this._characterName,
        });

        this._alternate = HLF.Random.bool();

    }

    private onCultureChangeComplete(pCulture: Culture): void {
        this._currentCulture = pCulture;
        this._noteParticleSystem.culture = pCulture;
    }

    private onIntroCompleted() {
        const nextCulture = (this._currentCulture + 1) % Culture.NumElements;
    }

    private onSoloStart(pCharacter: CharacterName): void {
        if (pCharacter !== this._characterName) {
            if (this._state === CharacterState.Idle) {
                this.switchState(CharacterState.Supporting);
            }
        }
    }

    private onSoloEnd(pCharacter: CharacterName): void {
        if (this._characterName === pCharacter) {
            if (this._queuedCulture !== Culture.NumElements) {
                this.switchState(CharacterState.Swapping);
            }
            else {
                this.switchState(CharacterState.Idle);
            }
            this._noteParticleSystem.stopEmitting();
            this.SpotlightOff();
            this._loadedSongName = "";
        }
        else {
            if (this._state === CharacterState.Supporting) {
                this.switchState(CharacterState.Idle);
            }
        }
    }

    private onDebugStateChanged(pInDebugMode: boolean): void {
        this._debugParticlePosition.visible = pInDebugMode;
    }

    /**
     * No harm in calling this for files that are already loaded. Callback will still fire.
     * @param pCulture
     * @param pAlternate
     * @param pCallback
     */
    private loadSolo(pCulture: Culture, pAlternate: boolean, pCallback?: (pSongName: string) => void) {
        const songToLoad = AudioPreloadManager.getFilename(pCulture, this._characterName, pAlternate);
        Application.instance.audio.load(songToLoad, Constants.AUDIO_CATEGORY_SOLO, () => {
            if (pCallback !== undefined) {
                pCallback(songToLoad);
            }
        });
    }

    private preloadSolo(pCulture: Culture, pAlternate: boolean, pPriority: number = 0): void {
        AudioPreloadManager.Enqueue({
            alternate: pAlternate,
            character: this._characterName,
            culture: pCulture,
            priority: pPriority,
        });
    }

}
